import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWaiterStore } from '../store/waiterStore';
import { createDeliveryOrder } from '../../api/restaurant/ordersApi';
import type { WaiterOrder } from '../types/waiter';

const API_BASE = import.meta.env.MODE === 'production' ? '/api' : 'http://localhost:3005/api';

export const WaiterOrderConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const { client, cart, tableNumber, waiter, clearCart, addOrder } = useWaiterStore();
  const [useBonuses, setUseBonuses] = useState(false);
  const [bonusAmount, setBonusAmount] = useState(0);
  const [comment, setComment] = useState('');
  const [takeaway, setTakeaway] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maxRedemptionPercent, setMaxRedemptionPercent] = useState(40);

  // Load bonus settings from admin
  useEffect(() => {
    fetch(`${API_BASE}/settings/bonuses`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.settings?.maxRedemptionPercent) {
          setMaxRedemptionPercent(data.settings.maxRedemptionPercent);
        }
      })
      .catch(() => {});
  }, []);

  const cartTotal = cart.reduce((sum, i) => sum + (i.price - (i.discount || 0)) * i.quantity, 0);
  const maxBonus = client ? Math.min(client.effectiveBalance, Math.floor(cartTotal * (maxRedemptionPercent / 100))) : 0;
  const finalTotal = cartTotal - (useBonuses ? bonusAmount : 0);

  const handleSubmit = async () => {
    if (!client || !waiter || cart.length === 0) return;

    setLoading(true);
    setError('');

    try {
      const result = await createDeliveryOrder({
        cartItems: cart.map(i => ({
          id: i.id,
          name: i.name,
          price: i.price,
          discount: i.discount || 0,
          quantity: i.quantity,
          schemeId: i.schemeId,
          description: i.description,
          imageUrls: i.imageUrls,
          measure: i.measure,
        })),
        phone: client.phone || '',
        expectedAt: '',
        paymentType: 'cash',
        comment: `Стол ${tableNumber}. ${takeaway ? 'С СОБОЙ. ' : ''}Официант: ${waiter.name}. ${comment}`.trim(),
        useBonusPayment: useBonuses && bonusAmount > 0,
        bonusAmount: useBonuses ? bonusAmount : 0,
        userInfo: {
          firstName: client.name.split(' ')[0] || 'Гость',
          lastName: client.name.split(' ').slice(1).join(' ') || '',
        },
      });

      // Create local order record
      const order: WaiterOrder = {
        id: Date.now().toString(),
        orderId: result.orderId,
        rkeeperOrderId: result.rkeeperOrderId,
        client,
        items: [...cart],
        tableNumber,
        total: finalTotal,
        bonusUsed: useBonuses ? bonusAmount : 0,
        status: 'pending',
        createdAt: new Date().toISOString(),
        waiterId: waiter.id,
        waiterName: waiter.name,
      };

      addOrder(order);
      clearCart();
      navigate(`/waiter/order/${order.id}`);
    } catch (err: any) {
      setError(err.message || 'Ошибка при создании заказа');
    } finally {
      setLoading(false);
    }
  };

  if (!client || cart.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">Корзина пуста</p>
        <button
          onClick={() => navigate('/waiter')}
          className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-xl"
        >
          На главную
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-slate-400 hover:text-white transition-colors"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Назад к меню
      </button>

      <h1 className="text-xl font-bold text-white">Подтверждение заказа</h1>

      {/* Client & table info */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4 flex items-center justify-between">
        <div>
          <p className="text-white font-medium">{client.name}</p>
          <p className="text-slate-400 text-sm">{client.phone || `ID: ${client.id}`}</p>
        </div>
        <div className="text-right">
          <p className="text-emerald-400 font-bold text-lg">Стол {tableNumber}</p>
        </div>
      </div>

      {/* Dine-in / Takeaway */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-3 flex items-center space-x-2">
        <button
          onClick={() => setTakeaway(false)}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            !takeaway ? 'bg-emerald-600 text-white' : 'bg-slate-700/50 text-slate-400'
          }`}
        >
          В зале
        </button>
        <button
          onClick={() => setTakeaway(true)}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            takeaway ? 'bg-orange-500 text-white' : 'bg-slate-700/50 text-slate-400'
          }`}
        >
          С собой
        </button>
      </div>

      {/* Cart items */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 divide-y divide-slate-700/50">
        {cart.map(item => (
          <div key={item.id} className="p-3 flex items-center justify-between">
            <div className="flex-1 min-w-0 mr-3">
              <p className="text-white text-sm">{item.name}</p>
              <p className="text-slate-400 text-xs">{item.quantity} x {item.price - (item.discount || 0)} ₽</p>
            </div>
            <p className="text-white font-medium text-sm">
              {(item.price - (item.discount || 0)) * item.quantity} ₽
            </p>
          </div>
        ))}
      </div>

      {/* Bonuses */}
      {client.effectiveBalance > 0 && (
        <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4 space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-white text-sm font-medium">Использовать бонусы</p>
              <p className="text-slate-400 text-xs">Доступно: {client.effectiveBalance} ₽ (макс. {maxBonus} ₽)</p>
            </div>
            <input
              type="checkbox"
              checked={useBonuses}
              onChange={(e) => {
                setUseBonuses(e.target.checked);
                if (e.target.checked) setBonusAmount(maxBonus);
              }}
              className="w-5 h-5 rounded text-emerald-600 bg-slate-700 border-slate-600 focus:ring-emerald-500"
            />
          </label>
          {useBonuses && (
            <input
              type="number"
              min="0"
              max={maxBonus}
              value={bonusAmount}
              onChange={(e) => setBonusAmount(Math.min(parseInt(e.target.value) || 0, maxBonus))}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          )}
        </div>
      )}

      {/* Comment */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4">
        <label className="block text-sm text-slate-400 mb-2">Комментарий к заказу</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Пожелания к приготовлению..."
          rows={2}
          className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
        />
      </div>

      {/* Total */}
      <div className="bg-slate-800/60 rounded-xl border border-emerald-500/30 p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Сумма</span>
          <span className="text-white">{cartTotal} ₽</span>
        </div>
        {useBonuses && bonusAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Бонусы</span>
            <span className="text-emerald-400">-{bonusAmount} ₽</span>
          </div>
        )}
        <div className="flex justify-between text-lg font-bold border-t border-slate-700/50 pt-2">
          <span className="text-white">Итого</span>
          <span className="text-emerald-400">{finalTotal} ₽</span>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm text-center">
          {error}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-semibold text-lg transition-colors"
      >
        {loading ? 'Отправка...' : 'Отправить на кухню'}
      </button>
    </div>
  );
};
